/**
 * Keyboard shortcuts.
 *
 * One table drives both the handler and the help panel, so the two cannot
 * disagree about what a key does.
 */

import * as ui from '../state/ui.ts';
import { showLibrary, showQueue, showSearch } from './browse.ts';
import {
  cycleRepeat,
  next,
  nudgeVolume,
  previous,
  seekBy,
  toggleMute,
  toggleShuffle,
  toggleTransport,
} from './commands.ts';
import {
  applyPitch,
  applyView,
  nudgePitch,
  resetView,
  toggleCinematic,
  togglePictureDisc,
  toggleRPM,
  toggleYouTubeCard,
} from './deck-commands.ts';
import { toggleFullscreen } from './fullscreen.ts';

export interface Shortcut {
  keys: string;
  description: string;
}

/** The list shown in the help panel, in the order it reads best. */
export const SHORTCUTS: readonly Shortcut[] = [
  { keys: 'Space', description: 'Play / pause' },
  { keys: '→ / ←', description: 'Seek ±5s (Shift: ±30s)' },
  { keys: '↑ / ↓', description: 'Volume' },
  { keys: 'N / P', description: 'Next / previous record' },
  { keys: 'M', description: 'Mute' },
  { keys: 'X', description: 'Shuffle' },
  { keys: 'R', description: 'Repeat (off → all → one)' },
  { keys: 'S', description: 'Search' },
  { keys: 'L', description: 'Library' },
  { keys: 'Q', description: 'Queue' },
  { keys: '1 / 2 / 3', description: 'Record / angled / front view' },
  { keys: '4', description: 'Close-up on the tonearm' },
  { keys: '0', description: 'Reset the view' },
  { keys: '[ / ]', description: 'Pitch fader down / up' },
  { keys: '\\', description: 'Reset pitch to 0' },
  { keys: 'T', description: 'Toggle 33⅓ / 45 RPM' },
  { keys: 'V', description: 'Picture-disc artwork on / off' },
  { keys: 'C', description: 'Cinematic record swap on / off' },
  { keys: 'G', description: 'Toggle the YouTube player card' },
  { keys: 'F', description: 'Full screen' },
  { keys: '?', description: 'This list' },
  { keys: 'Esc', description: 'Close panels' },
];

const SEEK_STEP = 5;
const SEEK_STEP_BIG = 30;
const VOLUME_STEP = 0.06;
const PITCH_STEP = 0.5;

type Action = (e: KeyboardEvent) => void;

/** Case-insensitive letter bindings. */
const LETTERS: Record<string, Action> = {
  n: () => void next(),
  p: () => void previous(),
  m: toggleMute,
  x: toggleShuffle,
  r: cycleRepeat,
  s: showSearch,
  l: showLibrary,
  q: showQueue,
  t: toggleRPM,
  v: togglePictureDisc,
  c: toggleCinematic,
  g: toggleYouTubeCard,
  f: toggleFullscreen,
};

const KEYS: Record<string, Action> = {
  ' ': () => void toggleTransport(),
  ArrowRight: (e) => seekBy(e.shiftKey ? SEEK_STEP_BIG : SEEK_STEP),
  ArrowLeft: (e) => seekBy(e.shiftKey ? -SEEK_STEP_BIG : -SEEK_STEP),
  ArrowUp: () => nudgeVolume(VOLUME_STEP),
  ArrowDown: () => nudgeVolume(-VOLUME_STEP),
  '1': () => applyView('top'),
  '2': () => applyView('angle'),
  '3': () => applyView('front'),
  '4': () => applyView('arm'),
  '0': resetView,
  '[': () => nudgePitch(-PITCH_STEP),
  ']': () => nudgePitch(PITCH_STEP),
  '\\': () => applyPitch(0, true),
  '?': () => ui.setModal('help'),
  Escape: () => {
    if (ui.menuOpen()) ui.setMenuOpen(false);
    else if (ui.modal()) ui.setModal(null);
    else if (ui.sheet().open) ui.closeSheet();
  },
};

/** True while the user is typing, when shortcuts must stand back. */
function isTyping(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
}

function onKeyDown(e: KeyboardEvent): void {
  if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
  const action = KEYS[e.key] ?? LETTERS[e.key.toLowerCase()];
  if (!action) return;
  e.preventDefault();
  action(e);
}

/** Wires the keyboard. Returns a teardown function. */
export function wireShortcuts(): () => void {
  addEventListener('keydown', onKeyDown);
  return () => {
    removeEventListener('keydown', onKeyDown);
  };
}
